import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import { formatPrice } from 'common/formatters';

import { getHouseholdPets, calculateHouseholdDepositFees } from 'common/householdHelpers';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrCard from 'client/components/SrCard/SrCard';

const SrEditLink = ({ onClick }) => (
  <SrText
    color="blue300"
    font="greycliff"
    weight="600"
    size="14"
    lineHeight="20px"
    cursor="pointer"
    onClick={onClick}
  >
    Edit
  </SrText>
);

const SrPartnerapprovalFormFeesCard = ({
  listing,
  concession,
  partnerSlug,
  householdListing,
  householdApplications,
  onEditStep,
}) => {
  const history = useHistory();

  const { nonEsaPets } = getHouseholdPets({
    householdApplications,
  });

  const feesForCharge = calculateHouseholdDepositFees({
    listing,
    concession,
    householdListing,
    pets: nonEsaPets,
  });

  const goToRenterPetCriteria = () => {
    history.push(
      `/partner/${partnerSlug}/listing/${listing.id}/edit/renter-pet-criteria?householdListingIdToApprove=${householdListing.id}&scrollTo=petDeposit`,
    ); // eslint-disable-line max-len
  };

  return (
    <SrCard header="Payments to Collect" noContentPadding>
      <SrBox margin="22px 20px 6px 20px" paddingBottom="16px">
        {feesForCharge.items.map((fee, index) => {
          const isLastElement = index === feesForCharge.items.length - 1;
          return (
            <SrBox key={fee.title} marginBottom={isLastElement ? null : '12px'}>
              <SrFlex justify="between">
                <SrFlex direction="col">
                  <SrText
                    font="greycliff"
                    weight="600"
                    color="blueGreycliff"
                    size="16"
                    lineHeight="25px"
                  >
                    {fee.title}
                  </SrText>
                  {fee.subtitle && (
                    <SrText
                      color="blueGreycliff"
                      font="greycliff"
                      weight="600"
                      size="14"
                      lineHeight="20px"
                      opacity="0.5"
                    >
                      {fee.subtitle}
                    </SrText>
                  )}
                  {fee.quantity && (
                    <SrText
                      color="blueGreycliff"
                      font="greycliff"
                      weight="600"
                      size="14"
                      lineHeight="20px"
                      opacity="0.5"
                    >
                      Quantity: {fee.quantity}
                    </SrText>
                  )}
                  {fee.category === 'deposit' && (
                    <SrText
                      color="blueGreycliff"
                      font="greycliff"
                      weight="600"
                      size="14"
                      lineHeight="20px"
                      opacity="0.5"
                    >
                      {householdListing.securityDepositIncreaseMultiplier}x Deposit Amount
                    </SrText>
                  )}
                </SrFlex>
                <SrFlex direction="col" align="end">
                  <SrText
                    font="greycliff"
                    weight="600"
                    color="blueGreycliff"
                    size="16"
                    lineHeight="30px"
                  >
                    {formatPrice(fee.value)}
                  </SrText>
                  {fee.category === 'deposit' && onEditStep && (
                    <SrEditLink onClick={() => onEditStep('security-deposit')} />
                  )}
                  {fee.category === 'pet' && onEditStep && (
                    <SrEditLink onClick={goToRenterPetCriteria} />
                  )}
                  {fee.category === 'rent' && onEditStep && (
                    <SrEditLink onClick={() => onEditStep('collect-rent')} />
                  )}
                </SrFlex>
              </SrFlex>
            </SrBox>
          );
        })}
      </SrBox>
      <SrBox padding="22px 20px 4px 20px " borderTop="1px solid rgba(123, 132, 136, 0.16)">
        <SrFlex justify="between">
          <SrText font="greycliff" weight="800" color="blueGreycliff" size="21" lineHeight="30px">
            Total to collect
          </SrText>
          <SrText font="greycliff" weight="800" color="green" size="21" lineHeight="30px">
            {formatPrice(feesForCharge.totalWithoutStripeFees)}
          </SrText>
        </SrFlex>
      </SrBox>
    </SrCard>
  );
};

SrPartnerapprovalFormFeesCard.propTypes = {
  listing: PropTypes.object.isRequired,
  householdListing: PropTypes.object.isRequired,
  householdApplications: PropTypes.array.isRequired,
  onEditStep: PropTypes.func,
};

SrPartnerapprovalFormFeesCard.displayName = 'SrPartnerapprovalFormFeesCard';

export default SrPartnerapprovalFormFeesCard;
